/* eslint-disable jsx-a11y/alt-text */
import React, { useEffect } from 'react';
import { observer } from 'mobx-react';
import { useHistory } from 'react-router-dom';

import styles from './releases.module.css';
import releasesStore from '../../stores/releases.store';
import loglyticsStore from '../../stores/loglytics.store';
import memberStore from '../../stores/member.store';
import commonStore from '../../stores/common.store';
import { IRelease, ReleaseSource } from '../../models/release.model';
import { PlatformUtil } from '../../models/platform.model';
import Time from '../../services/time.service';
import { LocalLoader } from '../common/loader.component';

import radar from '../../img/radar.gif';

function crashFreeColor(rate: number) {
  if (rate >= 0.995) return 'var(--tealish)';
  if (rate >= 0.98) return 'var(--mango)';
  return 'var(--lipstick)';
}

const SourceBadge = ({release}: {release: IRelease}) => {
  const badge = (
    <div className={`${styles.sourceBadge} ${styles[release.source]}`}>{release.source}</div>
  );
  if (release.source !== ReleaseSource.GITHUB || !release.github) return badge;
  return (
    <a href={release.github.url} target="_blank" rel="noreferrer"
      data-tip={`${release.github.repo} ${release.github.tag}`}>
      {badge}
    </a>
  );
};

const ReleaseRow = ({release}: {release: IRelease}) => {
  const history = useHistory();

  function showIssues() {
    const {accountId, appId} = memberStore;
    loglyticsStore.setVersion(release.version);
    history.push(`/${accountId}/${appId}/loglytics`);
  }

  const firstSeen = release.firstSeen ? new Time(release.firstSeen).getLongDateTime() : '—';
  const adoption = Math.round(release.adoption * 1000) / 10;
  const crashFree = release.crashFreeSessions;

  return (
    <div className={styles.row}>
      <div className={styles.version}>{release.version}</div>
      <div className={styles.source}><SourceBadge release={release} /></div>
      <div className={styles.firstSeen}>{firstSeen}</div>
      <div className={styles.adoption}>
        <div className={styles.adoptionBar}>
          <div className={styles.adoptionFill} style={{width: `${adoption}%`}} />
        </div>
        <div className={styles.adoptionValue}>{adoption}%</div>
      </div>
      <div className={styles.crashFree}
        style={crashFree !== undefined ? {color: crashFreeColor(crashFree)} : undefined}>
        {crashFree !== undefined ? `${Math.floor(crashFree * 1000) / 10}%` : '—'}
      </div>
      <div className={`${styles.issues} ${release.newIssues > 0 ? styles.issuesLink : ''}`}
        onClick={release.newIssues > 0 ? showIssues : undefined}>
        {release.newIssues}
      </div>
      <div className={styles.issues}>{release.regressedIssues}</div>
    </div>
  );
};

const Releases = observer(() => {
  useEffect(() => {
    releasesStore.getReleases();
  }, []);

  const {releases, loading} = releasesStore;
  const unit = PlatformUtil.isServer(memberStore.app?.platform) ? 'requests' : 'sessions';

  if (loading && !commonStore.loading) return <LocalLoader />;

  return (
    <div className={styles.component}>
      {releases.length > 0 ?
        <div className={styles.table}>
          <div className={styles.headerRow}>
            <div className={styles.version}>Version</div>
            <div className={styles.source}>Source</div>
            <div className={styles.firstSeen}>First seen</div>
            <div className={styles.adoption} title={`Share of ${unit} in the last 24 hours running this version`}>Adoption (24h)</div>
            <div className={styles.crashFree} title={`Share of this version's ${unit} without a crash over the last 7 full days (UTC)`}>Crash-free {unit} (7d)</div>
            <div className={styles.issues} title="Issues first seen in this version — over the release's lifetime">New issues</div>
            <div className={styles.issues} title="Fixed issues that reappeared in this version — over the release's lifetime">Regressed</div>
          </div>
          {releases.map(release => <ReleaseRow key={release.version} release={release} />)}
        </div>
        :
        !commonStore.loading && !loading &&
        <div className={styles.empty}>
          <img src={radar} />
          <div>
            <div>No releases on the radar yet...</div>
            <div>Releases appear automatically as soon as {unit} with a new app version arrive.{'\n'}
              You can also mark deploys from CI via the release API or the GitHub integration.</div>
          </div>
        </div>
      }
    </div>
  );
});

export default Releases;
